// Step 1 of the client-side X12 pipeline: turn raw EDI text into a flat array
// of segments. Nothing here understands what an ISA, GS, ST or PO1 *means* —
// that's the dictionary (lib/dictionary) and the loop structuring in
// lib/envelope.ts / lib/parser850.ts. This module only knows how to find the
// delimiters and split on them.
//
// The ISA segment is fixed-width by the X12 standard: 106 characters
// (including its own segment terminator) when every delimiter is a single
// character, which X12 requires. That fixed width is what lets the element
// separator, sub-element (component) separator and segment terminator be read
// directly off known character positions instead of guessed at.

/** A single X12 element's value — a plain string, or an array of
 * sub-elements when the element uses the component (sub-element) separator. */
export type EdiElementValue = string | string[];

export interface EdiSegment {
  /** The segment id, e.g. "ISA", "GS", "ST", "BEG", "PO1". */
  id: string;
  /** One entry per element after the id, in order (ISA01, ISA02, ... or
   * BEG01, BEG02, ...). 1-indexed access is the caller's job. */
  elements: EdiElementValue[];
}

export interface EdiDelimiters {
  /** Separates elements within a segment. Almost always "*". */
  element: string;
  /** Separates sub-elements (components) within an element. Read from ISA16. */
  subElement: string;
  /** Terminates a segment. Almost always "~". */
  segmentTerminator: string;
}

export interface TokenizeSuccess {
  ok: true;
  delimiters: EdiDelimiters;
  segments: EdiSegment[];
}

export interface TokenizeFailure {
  ok: false;
  error: string;
}

export type TokenizeResult = TokenizeSuccess | TokenizeFailure;

/** Length of a well-formed ISA segment, including its terminator, when every
 * delimiter is a single character (the only kind X12 allows). */
const ISA_SEGMENT_LENGTH = 106;
/** 0-indexed position of the element separator — the character immediately
 * following the literal "ISA". */
const ELEMENT_SEP_INDEX = 3;
/** 0-indexed position of ISA16, the component (sub-element) separator. */
const SUB_ELEMENT_SEP_INDEX = 104;
/** 0-indexed position of the segment terminator that ends the ISA segment. */
const SEGMENT_TERM_INDEX = 105;

function stripLeadingLineBreaks(value: string): string {
  return value.replace(/^[\r\n]+/, '');
}

function stripTrailingLineBreaks(value: string): string {
  return value.replace(/[\r\n]+$/, '');
}

/**
 * Parses raw EDI X12 text into delimiters plus a flat array of segments.
 * Never throws — a document that can't be tokenized (empty, not an ISA
 * envelope, or a truncated ISA) comes back as a typed failure so a bad
 * paste/upload can be shown as an inline error instead of blanking the page.
 */
export function tokenize(raw: string): TokenizeResult {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return {ok: false, error: 'Document is empty.'};
  }

  // Tolerate a BOM or blank lines before the interchange begins; everything
  // downstream is indexed off this trimmed text.
  const text = raw.replace(/^\uFEFF/, '').trimStart();

  if (!text.startsWith('ISA')) {
    return {
      ok: false,
      error:
        'Document does not start with an ISA segment — not an X12 interchange.',
    };
  }

  if (text.length < ISA_SEGMENT_LENGTH) {
    return {
      ok: false,
      error: `ISA segment is truncated — expected at least ${ISA_SEGMENT_LENGTH} characters, found ${text.length}.`,
    };
  }

  const delimiters: EdiDelimiters = {
    element: text[ELEMENT_SEP_INDEX],
    subElement: text[SUB_ELEMENT_SEP_INDEX],
    segmentTerminator: text[SEGMENT_TERM_INDEX],
  };

  if (
    !delimiters.element ||
    !delimiters.subElement ||
    !delimiters.segmentTerminator
  ) {
    return {
      ok: false,
      error: 'Could not detect delimiters from the ISA segment.',
    };
  }

  const rawPieces = text.split(delimiters.segmentTerminator);
  const segments: EdiSegment[] = [];

  for (const piece of rawPieces) {
    const cleaned = stripTrailingLineBreaks(stripLeadingLineBreaks(piece));
    if (cleaned.trim() === '') continue;

    const parts = cleaned.split(delimiters.element);
    const id = parts[0];
    const elements: EdiElementValue[] = parts
      .slice(1)
      .map(el =>
        el.includes(delimiters.subElement)
          ? el.split(delimiters.subElement)
          : el
      );
    segments.push({id, elements});
  }

  if (segments.length === 0) {
    return {ok: false, error: 'No segments found after the ISA envelope.'};
  }

  return {ok: true, delimiters, segments};
}
